import { z } from 'zod';

/** The `brand.dateFormat` column's ceiling; also what the admin's counter shows. */
export const DATE_PATTERN_MAX_LENGTH = 32;

/**
 * One pass of the pattern grammar: an escaped literal, a known token, or a
 * character that is not a letter (separators — `/`, `-`, `.`, `،`, a space).
 *
 * Brackets are excluded from the free-character class deliberately, so a stray
 * `[` fails here instead of reaching a formatter as an unterminated literal.
 * The alternatives are longest-first for the reason `DATE_PATTERN_TOKENS`
 * states: `YY` tried before `YYYY` would eat half of every year.
 */
const DATE_PATTERN_RULE = /^(?:\[[^\]]*\]|YYYY|YY|MMMM|MMM|MM|M|dddd|ddd|DD|D|[^A-Za-z[\]])+$/u;

/**
 * A brand date pattern.
 *
 * Written as `.regex()` rather than `.refine()`, and that is a hard constraint
 * rather than a style choice: `settings-metadata.service` projects every
 * definition's type through `z.toJSONSchema` in a single pass to build the whole
 * settings screen, and a refinement cannot be projected. A `pattern` in the
 * projection is also what lets `valueSchemaToZod` rebuild the same rule in the
 * admin form, so the operator is refused before the round trip rather than after
 * it.
 *
 * Until P10.2c this setting was `z.string().min(1).max(32)`, which accepted
 * `'hello'` — a date format that renders no date.
 */
export const DatePatternSchema = z
  .string()
  .min(1)
  .max(DATE_PATTERN_MAX_LENGTH)
  .regex(DATE_PATTERN_RULE);

export type DatePattern = z.infer<typeof DatePatternSchema>;

/** Whether a candidate is a usable pattern — the predicate, without the issue. */
export function isDatePattern(value: string): value is DatePattern {
  return DatePatternSchema.safeParse(value).success;
}
